import { isApiError } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatTimestamp,
  formatToman,
  type LocalDate,
  normalizeDigits,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  FactList,
  LoadMore,
  Ltr,
  MoneyField,
  PageHeader,
  Section,
  Skeleton,
  StatusChip,
  Tabs,
  TextArea,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import type {
  DelinquencyCase,
  DelinquencyCaseSummary,
  LateFeeWaiver,
  LateFeeWaiverCreate,
} from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar, TextFilterPill } from '../components/filter-pills.js';
import { CommandDialog } from '../components/command-dialog.js';
import { DetailHeader } from '../components/detail-header.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListFooter } from '../components/list-footer.js';
import { FormReasonChips } from '../components/reason-field.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { approvalSubmitLabel, canSeeArea, canUse } from '../permissions.js';
import {
  type DelinquencyCaseFilters,
  listStatus,
  setupKeys,
  useAdminSelf,
  useDelinquencyCase,
  useDelinquencyCases,
} from '../queries.js';

const REASON_MAX = 500;

type CollectionsTab = 'payments' | 'returns' | 'delinquency';

function CollectionsTabs({
  panel,
  value,
}: {
  readonly panel: ReactNode;
  readonly value: CollectionsTab;
}) {
  const navigate = useNavigate();
  return (
    <Tabs<CollectionsTab>
      label="بخش وصول"
      onChange={(next) =>
        void navigate(
          next === 'returns'
            ? '/return-requests'
            : next === 'delinquency'
              ? '/delinquency-cases'
              : '/payments',
        )
      }
      panel={panel}
      tabs={[
        { key: 'payments', label: 'پرداخت‌ها' },
        { key: 'returns', label: 'مرجوعی‌ها' },
        { key: 'delinquency', label: 'دیرکرد' },
      ]}
      value={value}
    />
  );
}

function caseDetailPath(pathname: string): boolean {
  return /^\/delinquency-cases\/[^/]+$/.test(pathname);
}

function localDate(value: string): LocalDate {
  return value as LocalDate;
}

function daysLabel(days: number): string {
  return `${formatCount(days)} روز`;
}

function yesNo(value: boolean): string {
  return value ? 'بله' : 'خیر';
}

function caseStatusChip(status: DelinquencyCaseSummary['status']) {
  switch (status) {
    case 'OPEN':
      return <StatusChip title="باز" tone="warning" />;
    case 'RESOLVED':
      return <StatusChip title="تسویه‌شده" tone="success" />;
    case 'CANCELLED':
      return <StatusChip title="لغوشده" />;
    default:
      return <StatusChip title="نامشخص" />;
  }
}

function resolutionValue(resolution: DelinquencyCase['resolution']): ReactNode {
  if (resolution === 'PAID') return <StatusChip enumName="installmentStatus" value="PAID" />;
  if (resolution === 'WAIVED_AND_PAID') {
    return <StatusChip title="بخشوده‌شده و پرداخت‌شده" tone="success" />;
  }
  return '—';
}

function entryChip(entryType: string) {
  const known: Record<
    string,
    { readonly title: string; readonly tone: 'info' | 'warning' | 'success' | 'danger' }
  > = {
    ASSESSMENT: { title: 'ثبت جریمه', tone: 'info' },
    RETURN_REVERSAL: { title: 'برگشت مرجوعی', tone: 'warning' },
    WAIVER: { title: 'بخشودگی', tone: 'success' },
    WRITE_OFF: { title: 'سوخت', tone: 'danger' },
  };
  const entry = known[entryType];
  return entry === undefined ? (
    <StatusChip title="نامشخص" />
  ) : (
    <StatusChip title={entry.title} tone={entry.tone} />
  );
}

function weekLabel(weekStart: string, weekEnd?: string): string {
  const start = formatLocalDate(localDate(weekStart));
  return weekEnd === undefined ? start : `${start} تا ${formatLocalDate(localDate(weekEnd))}`;
}

function integerDays(raw: string): number | undefined {
  const digits = normalizeDigits(raw).trim();
  if (!/^\d+$/.test(digits)) return undefined;
  return Number(digits);
}

export function DelinquencyCasesPage() {
  const location = useLocation();
  if (caseDetailPath(location.pathname)) {
    return <RequireArea area="payments">{() => <Outlet />}</RequireArea>;
  }
  return (
    <RequireArea area="payments">
      {() => (
        <>
          <DelinquencyList />
          <Outlet />
        </>
      )}
    </RequireArea>
  );
}

function DelinquencyList() {
  usePageTitle('پرونده‌های دیرکرد');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get('status') ?? '';
  const writtenOff = params.get('writtenOff') ?? '';
  const daysFromRaw = params.get('daysDelinquentFrom') ?? '';
  const daysFrom = integerDays(daysFromRaw);
  const filters: DelinquencyCaseFilters = {
    ...(status === '' ? {} : { status }),
    ...(writtenOff === 'true' || writtenOff === 'false' ? { writtenOff } : {}),
    ...(daysFrom === undefined ? {} : { daysDelinquentFrom: daysFrom }),
  };
  const list = useDelinquencyCases(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next);
  };
  const columns: readonly DataTableColumn<DelinquencyCaseSummary>[] = [
    {
      header: 'خریدار',
      id: 'buyer',
      pin: 'start',
      render: (row) => (
        <Link state={OPENED_FROM_LIST} to={`/delinquency-cases/${row.id}`}>
          {row.buyer.displayNameFa}
        </Link>
      ),
    },
    {
      header: 'هفته',
      id: 'week',
      render: (row) => weekLabel(row.weekStart, row.weekEnd),
    },
    {
      header: 'شروع دیرکرد',
      id: 'from',
      render: (row) => formatLocalDate(localDate(row.delinquentFrom)),
    },
    {
      header: 'روز معوق',
      id: 'days',
      render: (row) => daysLabel(row.daysDelinquent),
    },
    {
      header: 'اصل هفته',
      id: 'principal',
      render: (row) => formatToman(row.principalIrr),
    },
    {
      header: 'مانده وجه التزام',
      id: 'fee',
      render: (row) => formatToman(row.lateFee.outstandingIrr),
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => caseStatusChip(row.status),
    },
    {
      header: 'سوخت',
      id: 'writtenOff',
      render: (row) => yesNo(row.writtenOff),
    },
  ];
  const filtered = status !== '' || writtenOff !== '' || daysFromRaw !== '';
  return (
    <>
      <PageHeader
        description="هر پرونده یک هفته خریدار است: اصل پرداخت‌نشده، یک وجه التزام، و وضعیت وصول."
        title="پرونده‌های دیرکرد"
      />
      <CollectionsTabs
        panel={
          <>
            <Section id="delinquency-case-list" title="فهرست پرونده‌ها">
              <FilterPillBar
                activeFilterCount={
                  [status, writtenOff, daysFromRaw].filter((value) => value !== '').length
                }
                label="فیلتر پرونده‌های دیرکرد"
                onClearAll={() => {
                  const next = new URLSearchParams(params);
                  for (const key of ['status', 'writtenOff', 'daysDelinquentFrom'])
                    next.delete(key);
                  setParams(next);
                }}
              >
                <FilterPill
                  emptyLabel="همه وضعیت‌ها"
                  label="وضعیت"
                  onChange={(value) => setFilter('status', value)}
                  options={[
                    { label: 'باز', value: 'OPEN' },
                    { label: 'تسویه‌شده', value: 'RESOLVED' },
                    { label: 'لغوشده', value: 'CANCELLED' },
                  ]}
                  value={status}
                />
                <FilterPill
                  emptyLabel="همه وضعیت‌های سوخت"
                  label="سوخت"
                  onChange={(value) => setFilter('writtenOff', value)}
                  options={[
                    { label: 'سوخت‌شده', value: 'true' },
                    { label: 'سوخت‌نشده', value: 'false' },
                  ]}
                  value={writtenOff}
                />
                <TextFilterPill
                  inputMode="numeric"
                  label="حداقل روز معوق"
                  onApply={(value) => setFilter('daysDelinquentFrom', value)}
                  value={daysFromRaw}
                />
              </FilterPillBar>
              <QueryState
                empty={
                  <EmptyState
                    action={
                      filtered ? (
                        <Button onClick={() => setParams({})} variant="secondary">
                          پاک‌کردن فیلتر
                        </Button>
                      ) : undefined
                    }
                    title={
                      filtered ? 'پرونده‌ای با این فیلتر پیدا نشد' : 'پرونده دیرکردی ثبت نشده است'
                    }
                  />
                }
                error={list.error}
                isEmpty={rows.length === 0}
                onRetry={() => void list.refetch()}
                skeleton={<Skeleton label="در حال بارگذاری پرونده‌ها…" lines={4} />}
                status={listStatus(list)}
              >
                <AdminListTable
                  caption="فهرست پرونده‌های دیرکرد"
                  columns={columns}
                  mobileSummary={(row) => ({
                    primary: row.buyer.displayNameFa,
                    secondary: (
                      <>
                        {daysLabel(row.daysDelinquent)}
                        {' · '}
                        {formatToman(row.lateFee.outstandingIrr)}
                      </>
                    ),
                  })}
                  onRowClick={(row) =>
                    void navigate(`/delinquency-cases/${row.id}`, { state: OPENED_FROM_LIST })
                  }
                  regionLabel="جدول پرونده‌های دیرکرد"
                  rowKey={(row) => row.id}
                  rows={rows}
                />
                {list.isFetchNextPageError ? (
                  <ErrorState error={list.error} onRetry={() => void list.fetchNextPage()} />
                ) : null}
                <ListFooter
                  pager={
                    <LoadMore
                      hasMore={list.hasNextPage}
                      loading={list.isFetchingNextPage}
                      onLoadMore={() => void list.fetchNextPage()}
                    />
                  }
                  shown={rows.length}
                  total={undefined}
                />
              </QueryState>
            </Section>
          </>
        }
        value="delinquency"
      />
    </>
  );
}

function waiverSchema(outstandingIrr: number) {
  return z.object({
    amountIrr: z
      .number()
      .int()
      .nullable()
      .refine(
        (value) => value !== null && value >= 1 && value <= outstandingIrr,
        'مبلغ بخشودگی را تا سقف مانده وارد کنید.',
      ),
    reason: z
      .string()
      .trim()
      .min(1, 'دلیل را بنویسید.')
      .max(REASON_MAX, `حداکثر ${toPersianDigits(REASON_MAX)} کاراکتر.`),
  });
}

type WaiverValues = z.input<ReturnType<typeof waiverSchema>>;

function waiverGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'LATE_FEE_NOT_WAIVABLE':
      return typeof details.outstandingLateFeeIrr === 'number'
        ? `مانده وجه التزام ${formatToman(details.outstandingLateFeeIrr)} است.`
        : 'این وجه التزام قابل بخشودگی نیست.';
    case 'PAYMENT_IN_PROGRESS':
      return typeof details.paymentAttemptId === 'string' ? (
        <>
          {'پرداخت این هفته در جریان است. '}
          <Link to={`/payments/${details.paymentAttemptId}`}>مشاهدهٔ پرداخت</Link>
        </>
      ) : (
        'پرداخت این هفته در جریان است.'
      );
    default:
      return undefined;
  }
}

function LateFeeWaiverDialog({
  caseId,
  onOpenChange,
  outstandingIrr,
}: {
  readonly caseId: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly outstandingIrr: number;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const self = useAdminSelf().data;
  return (
    <CommandDialog<WaiverValues, LateFeeWaiverCreate, LateFeeWaiver>
      defaultValues={{ amountIrr: null, reason: '' }}
      description={`مانده وجه التزام ${formatToman(outstandingIrr)} است. بخشودگی روی همین پرونده است و قسط جدا نمی‌خواهد.`}
      fields={['amountIrr', 'reason']}
      guidance={waiverGuidance}
      name="delinquency.late-fee-waiver"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.delinquencyCases });
        await queryClient.invalidateQueries({ queryKey: setupKeys.delinquencyCase(caseId) });
        toast.success({
          confirmedBy: result,
          description: `مانده پس از بخشودگی ${formatToman(result.data.outstandingLateFeeIrr)}`,
          title: 'بخشودگی وجه التزام ثبت شد',
        });
        onOpenChange(false);
      }}
      schema={waiverSchema(outstandingIrr)}
      send={(client, body, intent) =>
        client.post(`/admin/delinquency-cases/${encodeURIComponent(caseId)}/late-fee-waivers`, {
          body,
          intent,
        })
      }
      submitLabel={self === undefined ? 'ثبت و اجرا' : approvalSubmitLabel(self)}
      title="بخشودگی وجه التزام"
      toBody={(values) => ({
        amountIrr: values.amountIrr as number,
        reason: values.reason.trim(),
      })}
    >
      {(form) => (
        <>
          <Controller
            control={form.control}
            name="amountIrr"
            render={({ field, fieldState }) => (
              <MoneyField
                error={fieldState.error?.message}
                label="مبلغ بخشودگی"
                maxRials={outstandingIrr}
                minRials={1}
                onChangeRials={field.onChange}
                valueRials={field.value}
              />
            )}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل"
            maxLength={REASON_MAX}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="delinquency.late-fee-waiver" />
        </>
      )}
    </CommandDialog>
  );
}

function personLine(name: string, mobile: string): ReactNode {
  return (
    <>
      {name}
      {' · '}
      <Ltr>{mobile}</Ltr>
    </>
  );
}

export function DelinquencyCasePage() {
  const { caseId = '' } = useParams();
  const [params] = useSearchParams();
  const query = useDelinquencyCase(caseId);
  const self = useAdminSelf();
  const data = query.data;
  const [waiverOpen, setWaiverOpen] = useState(false);
  usePageTitle(data?.buyer.displayNameFa ?? 'پرونده دیرکرد', true, 'پرونده دیرکرد');
  const caller = self.data;
  const canWaive =
    caller !== undefined &&
    canUse(caller, 'createLateFeeWaiver') &&
    data?.status === 'OPEN' &&
    data.lateFee.outstandingIrr >= 1;
  const search = params.toString();
  return (
    <>
      <QueryState
        error={query.error}
        onRetry={() => void query.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری پرونده…" lines={6} />}
        status={query.status}
      >
        {data === undefined || caller === undefined ? null : (
          <>
            <DetailHeader
              actions={
                canWaive ? (
                  <Button onClick={() => setWaiverOpen(true)} type="button">
                    بخشودگی وجه التزام
                  </Button>
                ) : undefined
              }
              actionsLabel="کارهای پرونده"
              back={{
                label: 'بازگشت به فهرست',
                state: OPENED_FROM_LIST,
                to: {
                  pathname: '/delinquency-cases',
                  ...(search === '' ? {} : { search: `?${search}` }),
                },
              }}
              meta={weekLabel(data.weekStart, data.weekEnd)}
              status={
                <>
                  {caseStatusChip(data.status)}
                  {data.writtenOff ? <StatusChip title="سوخت‌شده" tone="danger" /> : null}
                  {data.restricted ? <StatusChip title="محدود" tone="warning" /> : null}
                </>
              }
              title={data.buyer.displayNameFa}
            />
            {data.writtenOff ? (
              <Banner tone="warning">
                مطالبه این هفته سوخت شده است. اقساط معوق می‌مانند و وصول ادامه دارد.
              </Banner>
            ) : null}
            <Section id="delinquency-case-summary" title="خلاصه هفته">
              <FactList
                items={[
                  {
                    key: 'buyer',
                    label: 'خریدار',
                    value: canSeeArea(caller, 'buyers') ? (
                      <Link to={`/buyers/${data.buyer.buyerId}`}>
                        {personLine(data.buyer.displayNameFa, data.buyer.mobileMasked)}
                      </Link>
                    ) : (
                      personLine(data.buyer.displayNameFa, data.buyer.mobileMasked)
                    ),
                  },
                  {
                    key: 'from',
                    label: 'شروع دیرکرد',
                    value: formatLocalDate(localDate(data.delinquentFrom)),
                  },
                  { key: 'days', label: 'روز معوق', value: daysLabel(data.daysDelinquent) },
                  {
                    key: 'grace',
                    label: 'مهلت اعمال‌شده',
                    value: `${formatCount(data.graceDaysApplied)} روز`,
                  },
                  { key: 'principal', label: 'اصل هفته', value: formatToman(data.principalIrr) },
                  { key: 'payable', label: 'قابل پرداخت', value: formatToman(data.payableIrr) },
                  { key: 'purchases', label: 'خریدها', value: formatCount(data.purchaseCount) },
                  {
                    key: 'resolution',
                    label: 'نحوه تسویه',
                    value: resolutionValue(data.resolution),
                  },
                  {
                    key: 'model',
                    label: 'مدل وجه التزام',
                    value: statusLabel('lateFeeModel', data.lateFee.model),
                  },
                  {
                    key: 'assessed',
                    label: 'وجه التزام ارزیابی‌شده',
                    value: formatToman(data.lateFee.assessedIrr),
                  },
                  {
                    key: 'outstanding',
                    label: 'مانده وجه التزام',
                    value: formatToman(data.lateFee.outstandingIrr),
                  },
                  {
                    key: 'paidFee',
                    label: 'وجه التزام پرداخت‌شده',
                    value: formatToman(data.lateFee.paidIrr),
                  },
                  {
                    key: 'waived',
                    label: 'وجه التزام بخشوده‌شده',
                    value: formatToman(data.lateFee.waivedIrr),
                  },
                ]}
              />
            </Section>
            <Section id="delinquency-purchases" title="خریدهای هفته">
              {data.purchases.length === 0 ? (
                <EmptyState title="خریدی برای این هفته ثبت نشده است" />
              ) : (
                <AdminListTable
                  caption="خریدهای هفته"
                  columns={[
                    {
                      header: 'فروش',
                      id: 'sale',
                      render: (row) => <Ltr>{row.saleReference}</Ltr>,
                    },
                    {
                      header: 'فروشگاه',
                      id: 'merchant',
                      render: (row) => row.merchant.displayNameFa,
                    },
                    {
                      header: 'قسط',
                      id: 'sequence',
                      render: (row) => toPersianDigits(row.installment.sequence),
                    },
                    {
                      header: 'اصل',
                      id: 'principal',
                      render: (row) => formatToman(row.installment.currentPrincipalIrr),
                    },
                    {
                      header: 'وضعیت',
                      id: 'status',
                      render: (row) => (
                        <StatusChip enumName="installmentStatus" value={row.installment.status} />
                      ),
                    },
                  ]}
                  mobileSummary={(row) => ({
                    primary: row.saleReference,
                    secondary: row.merchant.displayNameFa,
                  })}
                  regionLabel="جدول خریدهای هفته"
                  rowKey={(row) => row.installment.installmentId}
                  rows={data.purchases}
                />
              )}
            </Section>
            <Section id="delinquency-fee-entries" title="اسناد وجه التزام">
              {data.lateFeeEntries.length === 0 ? (
                <EmptyState title="سندی برای وجه التزام ثبت نشده است" />
              ) : (
                <AdminListTable
                  caption="اسناد وجه التزام"
                  columns={[
                    {
                      header: 'نوع',
                      id: 'type',
                      render: (row) => entryChip(row.entryType),
                    },
                    {
                      header: 'مبلغ',
                      id: 'amount',
                      render: (row) => formatToman(row.amountIrr),
                    },
                    {
                      header: 'دلیل',
                      id: 'reason',
                      render: (row) => row.reason ?? '—',
                    },
                    {
                      header: 'ثبت‌کننده',
                      id: 'by',
                      render: (row) => row.createdBy?.displayNameFa ?? 'سامانه',
                    },
                    {
                      header: 'زمان',
                      id: 'at',
                      render: (row) => (
                        <time dateTime={row.createdAt}>{formatTimestamp(row.createdAt)}</time>
                      ),
                    },
                  ]}
                  mobileSummary={(row) => ({
                    primary: formatToman(row.amountIrr),
                    secondary: entryChip(row.entryType),
                  })}
                  regionLabel="جدول اسناد وجه التزام"
                  rowKey={(row) => row.id}
                  rows={data.lateFeeEntries}
                />
              )}
            </Section>
            <Section id="delinquency-restrictions" title="محدودیت">
              {data.restrictions.length === 0 ? (
                <EmptyState title="محدودیتی برای این پرونده ثبت نشده است" />
              ) : (
                <AdminListTable
                  caption="محدودیت پرونده"
                  columns={[
                    {
                      header: 'وضعیت',
                      id: 'status',
                      render: (row) => (
                        <StatusChip enumName="buyerRestrictionStatus" value={row.status} />
                      ),
                    },
                    {
                      header: 'شروع',
                      id: 'started',
                      render: (row) => (
                        <time dateTime={row.startedAt}>{formatTimestamp(row.startedAt)}</time>
                      ),
                    },
                    {
                      header: 'رفع',
                      id: 'released',
                      render: (row) =>
                        row.releasedAt === null ? (
                          '—'
                        ) : (
                          <time dateTime={row.releasedAt}>{formatTimestamp(row.releasedAt)}</time>
                        ),
                    },
                  ]}
                  mobileSummary={(row) => ({
                    primary: statusLabel('buyerRestrictionStatus', row.status),
                    secondary: formatTimestamp(row.startedAt),
                  })}
                  regionLabel="جدول محدودیت"
                  rowKey={(row) => row.restrictionId}
                  rows={data.restrictions}
                />
              )}
            </Section>
            <Section id="delinquency-guarantors" title="اطلاع‌رسانی ضامن">
              {data.guarantorNotifications.length === 0 ? (
                <EmptyState title="اطلاعیه‌ای برای ضامن ثبت نشده است" />
              ) : (
                <AdminListTable
                  caption="اطلاع‌رسانی ضامن"
                  columns={[
                    {
                      header: 'لایه',
                      id: 'layer',
                      render: (row) => `لایه ${toPersianDigits(row.layer)}`,
                    },
                    {
                      header: 'ضامن',
                      id: 'name',
                      render: (row) => row.guarantorDisplayNameFa,
                    },
                    {
                      header: 'محدودیت لایه ۱',
                      id: 'restriction',
                      render: (row) => (row.layer === 1 ? yesNo(row.restrictionApplied) : '—'),
                    },
                    {
                      header: 'زمان اطلاع',
                      id: 'at',
                      render: (row) =>
                        row.notifiedAt === null ? (
                          'ارسال نشده'
                        ) : (
                          <time dateTime={row.notifiedAt}>{formatTimestamp(row.notifiedAt)}</time>
                        ),
                    },
                  ]}
                  mobileSummary={(row) => ({
                    primary: row.guarantorDisplayNameFa,
                    secondary: `لایه ${toPersianDigits(row.layer)}`,
                  })}
                  regionLabel="جدول اطلاع‌رسانی ضامن"
                  rowKey={(row) => `${row.layer}-${row.guarantorBuyerId}`}
                  rows={data.guarantorNotifications}
                />
              )}
            </Section>
            <Section id="delinquency-payments" title="پرداخت‌ها">
              {data.payments.length === 0 ? (
                <EmptyState title="پرداختی برای این هفته ثبت نشده است" />
              ) : (
                <AdminListTable
                  caption="پرداخت‌های هفته"
                  columns={[
                    {
                      header: 'مبلغ',
                      id: 'amount',
                      render: (row) => (
                        <Link to={`/payments/${row.paymentId}`}>{formatToman(row.amountIrr)}</Link>
                      ),
                    },
                    {
                      header: 'وضعیت',
                      id: 'status',
                      render: (row) => (
                        <StatusChip enumName="paymentAttemptStatus" value={row.status} />
                      ),
                    },
                    {
                      header: 'زمان تأیید',
                      id: 'at',
                      render: (row) => (
                        <time dateTime={row.verifiedAt}>{formatTimestamp(row.verifiedAt)}</time>
                      ),
                    },
                  ]}
                  mobileSummary={(row) => ({
                    primary: formatToman(row.amountIrr),
                    secondary: statusLabel('paymentAttemptStatus', row.status),
                  })}
                  regionLabel="جدول پرداخت‌های هفته"
                  rowKey={(row) => row.paymentId}
                  rows={data.payments}
                />
              )}
            </Section>
            {data.writeOff === null ? null : (
              <Section id="delinquency-write-off" title="سوخت مطالبات">
                <FactList
                  items={[
                    {
                      key: 'principal',
                      label: 'اصل سوخت‌شده',
                      value: formatToman(data.writeOff.principalIrr),
                    },
                    {
                      key: 'fee',
                      label: 'وجه التزام سوخت‌شده',
                      value: formatToman(data.writeOff.lateFeeIrr),
                    },
                    {
                      key: 'at',
                      label: 'زمان ثبت',
                      value: (
                        <time dateTime={data.writeOff.postedAt}>
                          {formatTimestamp(data.writeOff.postedAt)}
                        </time>
                      ),
                    },
                  ]}
                />
              </Section>
            )}
          </>
        )}
      </QueryState>
      {waiverOpen && data !== undefined ? (
        <LateFeeWaiverDialog
          caseId={data.id}
          onOpenChange={setWaiverOpen}
          outstandingIrr={data.lateFee.outstandingIrr}
        />
      ) : null}
    </>
  );
}
